<template>
    <Modal close-esc-event="keydown" :close-button="false" :modal-class="modalClass" mask-class="grid" @closing="onModalClose">
        <template #toggle="scope">
            <slot v-bind="scope" />
        </template>

        <template #header="scope">
            <transition name="fade">
                <HelpWidgetButton v-if="form?.form?.help_widget_item && currentStepIndex !== 0" :item="form.form.help_widget_item" icon-size-class="size-6" class="z-50 absolute top-[15.5px] right-[58px]" />
            </transition>
            <CloseModalButton @close="scope.close"></CloseModalButton>
        </template>

        <template #body="scope">
            <form class="bg-white relative rounded-lg grid py-6 space-y-3 px-10 m-auto">
                <MultiStepFormHeaderIcon v-if="header || form.event" :icon-source="header?.icon_url || (form.event as any)?.icon_url || ''" :is-final-step="isFinalStep"></MultiStepFormHeaderIcon>
                <transition name="fade" mode="out-in">
                    <component
                        :is="currentStep.component"
                        v-model:state="form"
                        v-model:assignment="computedAssignment"
                        :header="header"
                        :config="currentStepConfig"
                        @submit="next"
                        @go-to-step="goToStep($event)"
                        @skip="goToStep(currentStepIndex + 2)"
                        @back="previous"
                        @close="scope.close"
                        @force-close="onForceClose(scope.close)"
                        @submission="$emit('submission')"
                    ></component>
                </transition>
            </form>
        </template>
    </Modal>
</template>

<script lang="ts">
import { defineComponent, Ref, computed, watch, ref } from 'vue';
import { Modal } from '@makeabledk/vue-ui/components/modal';
import { Form } from '@makeabledk/vue-ui/support/http';
import isEqual from 'lodash-es/isEqual';
import { useQueryClient } from '@tanstack/vue-query';
import { store } from '@/plugins/store';
import { ActionType } from '@/plugins/store/actions';
import { Assignment, MultiStepFormState } from '@/types';
import CustomButton from '@/components/ui/CustomButton.vue';
import DynamicForm from '@/components/common/multiStepFormSteps/DynamicForm.vue';
import LocationForm from '@/components/common/actionModal/LocationForm.vue';
import ConfirmationForm from '@/components/common/multiStepFormSteps/ConfirmationForm.vue';
import SubmissionStatusForm from '@/components/common/multiStepFormSteps/SubmissionStatusForm.vue';
import useMultiSteps from '@/components/common/composables/useMultiSteps';
import useResettableForm from '@/components/common/composables/useResettableForm';
import MultiStepFormHeaderIcon from '@/components/common/MultiStepFormHeaderIcon.vue';
import { i18n } from '@/plugins/internationalization/i18n';
import CloseModalButton from '@/components/common/CloseModalButton.vue';
import HelpWidgetButton from '@/components/common/helpWidget/HelpWidgetButton.vue';
import { queryKeys } from '@/plugins/store/actions/queries/notifications';

export default defineComponent({
    components: { Modal, CustomButton, MultiStepFormHeaderIcon, CloseModalButton, HelpWidgetButton },
    props: {
        stepConfig: {
            type: Array,
            default: () => [
                {},
                {},
                {
                    rejectable: false,
                    submitActionType: ActionType.PutForm,
                },
                {},
            ],
        },
        initialState: {
            type: Object as () => MultiStepFormState,
            default: () => ({
                location: null,
                form: null,
                confirmed: false,
                farm: null,
                record_id: null,
                notification: null,
                event: null,
            }),
        },
        initialStepIndex: {
            type: Number,
            default: 0,
        },
        header: {
            type: Object as () => null | { icon_url: string | number; text: string },
            default: null,
        },
        assignment: {
            type: Object as () => null | Assignment,
            default: null,
        },
    },
    emits: ['closing', 'submission', 'update:assignment'],
    setup(props, context) {
        const queryClient = useQueryClient();
        const resettableForm = useResettableForm().setInitial(props.initialState);
        const form = resettableForm.getInstance() as Ref<Form<MultiStepFormState> & MultiStepFormState>;

        watch(
            () => props.initialState,
            (newValue) => {
                if (newValue) {
                    resettableForm.setInitial(props.initialState);
                }
            }
        );

        const multiSteps = useMultiSteps([{ component: LocationForm }, { component: DynamicForm }, { component: ConfirmationForm }, { component: SubmissionStatusForm }]).setInitial(props.initialStepIndex);
        const currentStepConfig = computed(() => props.stepConfig[multiSteps.currentStepIndex.value] || {});

        const handleNotificationFormClose = async () => {
            if (!form.value?.notification) {
                return;
            }

            try {
                if (form.value?.notification?.read !== true) {
                    await store.dispatch(ActionType.EditNotification, { form: { notification: form.value.notification, update: [{ value: true, path: '/read', op: 'replace' }] } });
                }

                await Promise.all([queryClient.invalidateQueries({ queryKey: queryKeys.notifications }), store.dispatch(ActionType.GetMeta, { options: { ignoreCache: true, skipLoadingIndicator: true } })]);
            } catch (err) {
                //
            }
        };

        // eslint-disable-next-line no-unused-vars
        const onModalClose = (next: (closeModal: boolean) => boolean) => {
            if (isForceClosing.value) {
                isForceClosing.value = false;
            } else if ([1, 2].includes(multiSteps.currentStepIndex.value) && !isEqual(resettableForm.getInitial().form, form.value.form)) {
                if (!window.confirm(i18n.global.t('global.forms.confirmCloseModalMessage'))) {
                    return next(false);
                }
            }

            handleNotificationFormClose();
            context.emit('closing', multiSteps.isFinalStep.value);
            multiSteps.reset();
            resettableForm.reset();
            return true;
        };

        const isForceClosing = ref(false);
        const onForceClose = (close: CallableFunction) => {
            isForceClosing.value = true;
            close();
        };

        const computedAssignment = computed({
            get() {
                return props.assignment;
            },
            set(value) {
                context.emit('update:assignment', value);
            },
        });

        // A form laid out in more than the default two columns needs a wider modal
        const modalClass = computed(() => {
            const columns = form.value?.form?.columns ?? 2;
            let width = 'w-128';
            if (columns >= 6) {
                width = 'w-288 max-w-full';
            } else if (columns === 5) {
                // A column less than the six column modal
                width = 'w-240 max-w-full';
            } else if (columns > 2) {
                width = 'w-228 max-w-full';
            }
            return `${width} m-auto border-0`;
        });

        return { form, ...multiSteps, onModalClose, currentStepConfig, computedAssignment, onForceClose, modalClass };
    },
});
</script>
